import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import AuthDialog from "../components/AuthDialog";

const emptyIngredient = () => ({ name: "", quantity: "", seasonal_note: "", sourcing_tip: "" });
const emptyStep = (n) => ({ number: n, title: "", instruction: "", duration_min: 10 });
const emptyPairing = (type) => ({ type, name: "", description: "" });

const Submit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);

  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    region: "",
    season: "spring",
    course: "main",
    difficulty: "intermediate",
    prep_time_min: 30,
    cook_time_min: 45,
    servings: 2,
    story: "",
    plating_notes: "",
    tasting_notes: "",
  });
  const [ingredients, setIngredients] = useState([emptyIngredient(), emptyIngredient()]);
  const [steps, setSteps] = useState([emptyStep(1), emptyStep(2)]);
  const [pairings, setPairings] = useState([
    emptyPairing("wine"),
    emptyPairing("beer"),
    emptyPairing("non_alcoholic"),
  ]);
  const [tips, setTips] = useState([""]);
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        prep_time_min: Number(form.prep_time_min),
        cook_time_min: Number(form.cook_time_min),
        servings: Number(form.servings),
        ingredients: ingredients.filter((i) => i.name && i.quantity),
        steps: steps
          .filter((s) => s.title && s.instruction)
          .map((s, idx) => ({ ...s, number: idx + 1, duration_min: Number(s.duration_min) || null })),
        pairings: pairings.filter((p) => p.name && p.description),
        chef_tips: tips.filter((t) => t.trim()),
      };
      const res = await api.post("/recipes", payload);
      toast.success("Recipe submitted");
      navigate(`/recipe/${res.data.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit recipe";
      toast.error(typeof msg === "string" ? msg : "Please fill required fields");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-32 bg-stone-950">
      <div className="max-w-4xl mx-auto px-6 sm:px-10">
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <span className="magazine-number text-5xl">04</span>
            <div className="w-16 h-px bg-orange-600" />
            <span className="eyebrow">Submit a Composition</span>
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl md:text-7xl text-stone-100 leading-[0.95] sm:leading-[0.9] tracking-tight sm:tracking-tighter">
            Something you cooked? <span className="italic text-orange-600">Share it</span>.
          </h1>
          <p className="mt-8 text-stone-300 text-lg leading-relaxed max-w-2xl">
            The best recipes come from actual kitchens, not test kitchens. If you&apos;ve got
            a dish you keep coming back to — proper, honest, worth cooking — write it up here.
          </p>
        </div>

        {!user && (
          <div className="mb-12 border border-orange-700/60 bg-orange-950/20 p-6">
            <p className="text-stone-200">
              You&apos;ll need an account to submit.{" "}
              <button
                onClick={() => setAuthOpen(true)}
                data-testid="submit-signin-prompt"
                className="text-orange-500 ink-link uppercase text-xs tracking-[0.2em]"
              >
                Sign in
              </button>
            </p>
          </div>
        )}

        <form onSubmit={submit} className="space-y-16">
          {/* Basics */}
          <section className="border-t border-stone-800 pt-12 space-y-8">
            <SectionHead n="A" label="The Idea" />
            <Field label="Title" value={form.title} onChange={(v) => upd("title", v)} required testid="sub-title" />
            <Field label="Subtitle / hook" value={form.subtitle} onChange={(v) => upd("subtitle", v)} testid="sub-subtitle" />
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Region" value={form.region} onChange={(v) => upd("region", v)} required testid="sub-region" />
              <SelectField label="Season" value={form.season} onChange={(v) => upd("season", v)} testid="sub-season" options={["spring", "summer", "autumn", "winter"]} />
              <SelectField label="Course" value={form.course} onChange={(v) => upd("course", v)} testid="sub-course" options={["amuse-bouche", "palate-cleanser", "starter", "main", "dessert"]} />
              <SelectField label="Difficulty" value={form.difficulty} onChange={(v) => upd("difficulty", v)} testid="sub-difficulty" options={["approachable", "intermediate", "master"]} />
              <NumField label="Prep (min)" value={form.prep_time_min} onChange={(v) => upd("prep_time_min", v)} testid="sub-prep" />
              <NumField label="Cook (min)" value={form.cook_time_min} onChange={(v) => upd("cook_time_min", v)} testid="sub-cook" />
              <NumField label="Servings" value={form.servings} onChange={(v) => upd("servings", v)} testid="sub-servings" />
            </div>
            <TextArea label="Story" value={form.story} onChange={(v) => upd("story", v)} testid="sub-story" />
          </section>

          {/* Ingredients */}
          <section className="border-t border-stone-800 pt-12">
            <SectionHead n="B" label="The Pantry" />
            <div className="space-y-6 mt-8">
              {ingredients.map((ing, i) => (
                <div key={i} className="grid md:grid-cols-[1fr_120px_1fr_1fr_auto] gap-3 items-start">
                  <input value={ing.name} onChange={(e) => setIngredients((arr) => arr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Ingredient" data-testid={`sub-ing-name-${i}`} className={inputCls} />
                  <input value={ing.quantity} onChange={(e) => setIngredients((arr) => arr.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} placeholder="200g" data-testid={`sub-ing-qty-${i}`} className={inputCls} />
                  <input value={ing.seasonal_note} onChange={(e) => setIngredients((arr) => arr.map((x, j) => j === i ? { ...x, seasonal_note: e.target.value } : x))} placeholder="Seasonal note" className={inputCls} />
                  <input value={ing.sourcing_tip} onChange={(e) => setIngredients((arr) => arr.map((x, j) => j === i ? { ...x, sourcing_tip: e.target.value } : x))} placeholder="Sourcing tip" className={inputCls} />
                  <button type="button" onClick={() => setIngredients((arr) => arr.filter((_, j) => j !== i))} className="text-stone-500 hover:text-orange-500 px-3 py-3">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => setIngredients((arr) => [...arr, emptyIngredient()])} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-orange-500 hover:text-orange-400 ink-link" data-testid="sub-add-ingredient">
                <Plus className="w-3.5 h-3.5" /> Add ingredient
              </button>
            </div>
          </section>

          {/* Steps */}
          <section className="border-t border-stone-800 pt-12">
            <SectionHead n="C" label="The Method" />
            <div className="space-y-6 mt-8">
              {steps.map((s, i) => (
                <div key={i} className="grid md:grid-cols-[80px_1fr_120px_auto] gap-3 items-start">
                  <div className="magazine-number text-4xl">{String(i + 1).padStart(2, "0")}</div>
                  <div className="space-y-2">
                    <input value={s.title} onChange={(e) => setSteps((arr) => arr.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} placeholder="Step title" data-testid={`sub-step-title-${i}`} className={inputCls} />
                    <textarea value={s.instruction} onChange={(e) => setSteps((arr) => arr.map((x, j) => j === i ? { ...x, instruction: e.target.value } : x))} placeholder="Detailed instructions" data-testid={`sub-step-inst-${i}`} rows={3} className={inputCls} />
                  </div>
                  <input type="number" value={s.duration_min} onChange={(e) => setSteps((arr) => arr.map((x, j) => j === i ? { ...x, duration_min: e.target.value } : x))} placeholder="Min" className={inputCls} />
                  <button type="button" onClick={() => setSteps((arr) => arr.filter((_, j) => j !== i))} className="text-stone-500 hover:text-orange-500 px-3 py-3">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => setSteps((arr) => [...arr, emptyStep(arr.length + 1)])} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-orange-500 hover:text-orange-400 ink-link" data-testid="sub-add-step">
                <Plus className="w-3.5 h-3.5" /> Add step
              </button>
            </div>
          </section>

          {/* Notes */}
          <section className="border-t border-stone-800 pt-12 space-y-8">
            <SectionHead n="D" label="Plating & Tasting" />
            <TextArea label="Plating notes" value={form.plating_notes} onChange={(v) => upd("plating_notes", v)} testid="sub-plating" />
            <TextArea label="Tasting notes" value={form.tasting_notes} onChange={(v) => upd("tasting_notes", v)} testid="sub-tasting" />
          </section>

          {/* Pairings */}
          <section className="border-t border-stone-800 pt-12">
            <SectionHead n="E" label="The Pour" />
            <div className="space-y-6 mt-8">
              {pairings.map((p, i) => (
                <div key={i} className="grid md:grid-cols-[130px_1fr_2fr] gap-3">
                  <select value={p.type} onChange={(e) => setPairings((arr) => arr.map((x, j) => j === i ? { ...x, type: e.target.value } : x))} className={inputCls} data-testid={`sub-pair-type-${i}`}>
                    <option value="wine">Wine</option>
                    <option value="beer">Beer</option>
                    <option value="cider">Cider</option>
                    <option value="non_alcoholic">Zero-Proof</option>
                  </select>
                  <input value={p.name} onChange={(e) => setPairings((arr) => arr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Name" data-testid={`sub-pair-name-${i}`} className={inputCls} />
                  <input value={p.description} onChange={(e) => setPairings((arr) => arr.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} placeholder="Why it pairs" className={inputCls} />
                </div>
              ))}
            </div>
          </section>

          {/* Chef tips */}
          <section className="border-t border-stone-800 pt-12">
            <SectionHead n="F" label="Chef's Marginalia" />
            <div className="space-y-3 mt-8">
              {tips.map((t, i) => (
                <div key={i} className="flex gap-3">
                  <input value={t} onChange={(e) => setTips((arr) => arr.map((x, j) => j === i ? e.target.value : x))} placeholder="A short tip" className={inputCls} data-testid={`sub-tip-${i}`} />
                  <button type="button" onClick={() => setTips((arr) => arr.filter((_, j) => j !== i))} className="text-stone-500 hover:text-orange-500 px-3">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => setTips((arr) => [...arr, ""])} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-orange-500 ink-link">
                <Plus className="w-3.5 h-3.5" /> Add tip
              </button>
            </div>
          </section>

          <div className="pt-8">
            <button
              type="submit"
              disabled={busy}
              data-testid="sub-submit"
              className="inline-flex items-center gap-3 bg-orange-700 hover:bg-orange-800 disabled:opacity-70 text-stone-50 px-10 py-5 uppercase tracking-[0.28em] text-xs font-medium transition-colors"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Publish recipe
            </button>
          </div>
        </form>
      </div>
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
};

const inputCls = "w-full bg-stone-900 border border-stone-800 focus:border-orange-600 outline-none px-4 py-3 text-stone-100 placeholder-stone-600 transition-colors text-sm";

const SectionHead = ({ n, label }) => (
  <div className="flex items-center gap-4">
    <span className="magazine-number text-3xl">{n}</span>
    <span className="eyebrow">{label}</span>
  </div>
);

const Field = ({ label, value, onChange, required, testid }) => (
  <div>
    <label className="eyebrow block mb-2">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} className={inputCls} />
  </div>
);

const NumField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="eyebrow block mb-2">{label}</label>
    <input type="number" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={inputCls} />
  </div>
);

const TextArea = ({ label, value, onChange, testid }) => (
  <div>
    <label className="eyebrow block mb-2">{label}</label>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={4} data-testid={testid} className={inputCls} />
  </div>
);

const SelectField = ({ label, value, onChange, testid, options }) => (
  <div>
    <label className="eyebrow block mb-2">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-stone-900 border-stone-800 rounded-none text-stone-100">
        {options.map((o) => (
          <SelectItem key={o} value={o} className="rounded-none capitalize">{o.replace("-", " ")}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export default Submit;
